import React, { useEffect, useMemo, useState } from "react";
import { Modal, Avatar, Tag, Progress, Button, Empty, Spin, Alert } from "antd";
import {
  BarChartOutlined,
  DownOutlined,
  UpOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { TeacherTasksAPI } from "../../../../api/teacherTasksAPI";
import { BrandThemeProvider } from "../../../../ui";
import { useTranslation } from "react-i18next";
import styles from "./TaskResultsModal.module.scss";

/**
 * TaskResultsModal — "Ver detalles" de una tarea (rediseno AntD, F3).
 * Homogeneo con TaskDetailModal: cabecera limpia, tarjeta por alumno con
 * avatar + estado + progreso, y pasos con miniatura + modo + respuesta.
 *
 * Consume GET /teachertasks/results/:taskId (escenas ya enriquecidas).
 */
export function TaskResultsModal({ open, onClose, accessToken, taskId }) {
  const { t } = useTranslation("tasks");
  const api = useMemo(() => new TeacherTasksAPI(), []);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [task, setTask] = useState(null);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!open || !taskId) return undefined;
    let mounted = true;

    (async () => {
      setLoading(true);
      setLoadErr(null);
      try {
        const { task: taskData, results: r } = await api.getTaskResults(
          accessToken,
          taskId,
        );
        if (!mounted) return;
        setTask(taskData || null);
        setResults(Array.isArray(r) ? r : []);
      } catch (e) {
        if (mounted) setLoadErr(e?.message || t("results.loadError"));
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open, taskId, accessToken, api, t]);

  return (
    <BrandThemeProvider>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={720}
        centered
        className={styles.modal}
        styles={{ body: { padding: 0 } }}
        destroyOnHidden
      >
        {/* Cabecera */}
        <div className={styles.header}>
          <span className={styles.headerIcon}>
            <BarChartOutlined />
          </span>
          <div className={styles.headerText}>
            <h3 className={styles.title}>
              {task?.title
                ? t("results.titleWithTask", { taskTitle: task.title })
                : t("results.title")}
            </h3>
            {task?.classnumber ? (
              <div className={styles.sub}>
                {t("results.classLabel")} <strong>{task.classnumber}</strong>
              </div>
            ) : null}
          </div>
        </div>

        {/* Cuerpo */}
        <div className={styles.body}>
          {loading ? (
            <div className={styles.center}>
              <Spin />
            </div>
          ) : loadErr ? (
            <Alert type="error" showIcon message={loadErr} />
          ) : results.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("results.noResultsBody")}
            />
          ) : (
            <div className={styles.list}>
              {results.map((res, idx) => (
                <StudentResultCard
                  key={res?.student?._id || idx}
                  result={res}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button onClick={onClose}>{t("results.closeButton")}</Button>
        </div>
      </Modal>
    </BrandThemeProvider>
  );
}

/* ---------- Tarjeta por alumno ---------- */
function StudentResultCard({ result }) {
  const { t } = useTranslation("tasks");
  const [openStep, setOpenStep] = useState(null);

  const st = result.student || {};
  const displayName =
    st.name ||
    [st.firstname, st.lastname || st.surname].filter(Boolean).join(" ") ||
    st.email ||
    t("results.studentLabel");

  const pct = Number(result.progressPct || 0);

  const statusInfo = getStatusInfo(result.status, t);

  return (
    <div className={styles.student}>
      <div className={styles.studentHead}>
        <Avatar size={40} src={st.avatar}>
          {displayName.charAt(0)}
        </Avatar>
        <div className={styles.studentMeta}>
          <div className={styles.studentName}>{displayName}</div>
          <Progress
            percent={pct}
            size="small"
            showInfo={false}
            strokeColor="#00a75a"
            className={styles.progress}
          />
        </div>
        <div className={styles.studentRight}>
          <span className={`${styles.statusPill} ${styles[statusInfo.cls]}`}>
            {statusInfo.label}
          </span>
          <span className={styles.pct}>{pct}%</span>
        </div>
      </div>

      <div className={styles.steps}>
        {(result.steps || []).map((step) => {
          const title =
            step.sceneTitle ||
            `${t("editor.sceneDefault")} ${String(step.stepId).slice(-4)}`;
          const banner = step.sceneBanner || null;
          const isOpen = openStep === String(step.stepId);
          const hasAnswer = !!step.answer;

          return (
            <div key={String(step.stepId)} className={styles.step}>
              <div className={styles.stepMain}>
                <div className={styles.thumb}>
                  {banner ? (
                    <img src={banner} alt={title} />
                  ) : (
                    <PictureOutlined className={styles.thumbIcon} />
                  )}
                </div>

                <div className={styles.stepInfo}>
                  <div className={styles.stepTitle}>{title}</div>
                  <div className={styles.stepMeta}>
                    {hasAnswer ? (
                      <>
                        <Tag className={styles.modeTag}>{step.answer.mode}</Tag>
                        {step.answer.submittedAt ? (
                          <span className={styles.date}>
                            {formatDate(step.answer.submittedAt)}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <span className={styles.noAnswer}>
                        {t("results.noAnswer")}
                      </span>
                    )}
                  </div>
                </div>

                {hasAnswer && step.answer.text ? (
                  <Button
                    type="text"
                    size="small"
                    className={styles.viewBtn}
                    icon={isOpen ? <UpOutlined /> : <DownOutlined />}
                    onClick={() =>
                      setOpenStep(isOpen ? null : String(step.stepId))
                    }
                  >
                    {isOpen ? t("list.hide") : t("list.view")}
                  </Button>
                ) : null}
              </div>

              {isOpen && hasAnswer ? (
                <div className={styles.answer}>
                  <span className={styles.answerLabel}>
                    {t("results.answerLabel")}
                  </span>
                  <span className={styles.answerText}>
                    {step.answer.text || t("results.noText")}
                  </span>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Utils ---------- */
function getStatusInfo(status, t) {
  switch (status) {
    case "completed":
      return { label: t("results.statusCompleted"), cls: "stCompleted" };
    case "in_progress":
      return { label: t("results.statusInProgress"), cls: "stProgress" };
    case "not_started":
      return { label: t("results.statusNotStarted"), cls: "stPending" };
    default:
      return { label: status || "-", cls: "stPending" };
  }
}

function formatDate(d) {
  try {
    return new Date(d).toLocaleString();
  } catch {
    return d;
  }
}

export default TaskResultsModal;
